// Permission catalog shared by the Settings UI (owner configures) and the
// AuthContext `can()` helper (app enforces). The backend is the real security
// boundary and must enforce these too; the frontend only reflects/configures them.

export const PERMISSION_GROUPS = [
  {
    group: 'Inventory',
    perms: [
      { key: 'stone.create',  label: 'Add stones' },
      { key: 'stone.import',  label: 'Import stones (bulk)' },
      { key: 'stone.edit',    label: 'Edit stones' },
      { key: 'stone.delete',  label: 'Delete stones' },
      { key: 'jewelry.create',label: 'Add jewelry' },
      { key: 'barcode.print', label: 'Print barcodes' },
    ],
  },
  {
    group: 'Sales & memos',
    perms: [
      { key: 'invoice.create',  label: 'Create invoices' },
      { key: 'invoice.edit',    label: 'Edit / cancel invoices' },
      { key: 'memo.issue',      label: 'Issue memos' },
      { key: 'memo.return',     label: 'Process memo returns' },
      { key: 'transfer.create', label: 'Transfer stones between branches' },
    ],
  },
  {
    group: 'Customers & vendors',
    perms: [
      { key: 'customer.manage', label: 'Add / edit customers' },
      { key: 'vendor.manage',   label: 'Manage vendors' },
      { key: 'vendor.payments', label: 'Record vendor payments' },
    ],
  },
  {
    group: 'Finance',
    perms: [
      { key: 'expense.manage', label: 'Manage expenses' },
    ],
  },
  {
    group: 'Visibility',
    perms: [
      { key: 'price.buying.view', label: 'View buying price' },
      { key: 'reports.view',      label: 'View reports' },
    ],
  },
];
// Note: viewing the activity log is hard owner-only (not a grantable permission),
// so it is intentionally absent from this catalog and gated by role instead.

export const ALL_PERMISSION_KEYS = PERMISSION_GROUPS.flatMap(g => g.perms.map(p => p.key));

// Sensible defaults per role, used both as the AuthContext fallback (before the
// backend returns explicit overrides) and to seed the Settings toggles for a user
// who has no stored permissions yet. Owner is always all-true and not editable.
const DENY_BY_ROLE = {
  accountant: ['stone.delete'],
  branch_manager: ['stone.delete', 'price.buying.view', 'vendor.manage', 'vendor.payments', 'invoice.edit'],
  sales: ['stone.create', 'stone.import', 'stone.edit', 'stone.delete', 'price.buying.view',
          'vendor.manage', 'vendor.payments', 'invoice.edit', 'reports.view', 'expense.manage'],
};

export function defaultPermsFor(role) {
  const deny = role === 'owner' ? [] : (DENY_BY_ROLE[role] || []);
  return Object.fromEntries(ALL_PERMISSION_KEYS.map(k => [k, role === 'owner' ? true : !deny.includes(k)]));
}

// Effective permission map for a user: owner -> everything, otherwise role
// defaults overlaid with any explicit overrides the backend stored.
export function effectivePerms(user) {
  if (!user) return {};
  if (user.role === 'owner') return Object.fromEntries(ALL_PERMISSION_KEYS.map(k => [k, true]));
  const base = defaultPermsFor(user.role);
  const overrides = (user.permissions && typeof user.permissions === 'object') ? user.permissions : {};
  return { ...base, ...overrides };
}
